import type { Instrument, Timeframe } from "@trader/shared/MarketData"
import { type AccountSummary, type Product, type Runner, type StartPolicy, StrategyId } from "@trader/shared/Money"
import { Result } from "effect"
import { useEffect, useMemo, useState } from "react"
import { call, errorTag } from "../../api.ts"
import { tradingApi } from "../trading/tradingApi.ts"
import { accountsStore, collapsedPreference, tabPreference } from "../trading/tradingData.ts"
import { shownInstruments } from "../workspace/workspaceModel.ts"
import { currentWorkspace, type StrategyInstance } from "../workspace/workspaceStore.ts"
import {
  attachErrorText,
  attachRequest,
  canShort,
  draftKeyOfStrategy,
  eligibleAccounts,
  type PaperTradeTarget,
  paperTradeTarget,
  productsFor,
  runnerTimeframe,
  SAVE_FIRST
} from "./attachRunner.ts"
import { resolveUserScript } from "./strategySource.ts"
import { testerPanel } from "./testerPanel.ts"

/** A draft's target: its text against the strategy's latest saved version. */
const draftTarget = async (strategyId: string, key: string): Promise<PaperTradeTarget> => {
  if (key === "new") return paperTradeTarget(strategyId, null, null)
  const [script, detail] = await Promise.all([
    resolveUserScript(strategyId),
    call((client) => client.strategies.get({ params: { id: StrategyId.make(key) } }))
  ])
  const latest = { strategyId: key, version: detail.latest.version, source: detail.latest.source }
  return paperTradeTarget(strategyId, script.code.kind === "source" ? script.code.source : null, latest)
}

/** The "Paper trade…" dialog's state: the accounts to pick from, the choices, and the attach call. */
export const useAttachRunner = (strategy: StrategyInstance, instrument: Instrument, chartTimeframe: Timeframe) => {
  const [accounts, setAccounts] = useState<ReadonlyArray<AccountSummary> | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [accountId, setAccountId] = useState<string>("")
  const [product, setProduct] = useState<Product>("Cash")
  const [startPolicy, setStartPolicy] = useState<StartPolicy>("waitForFlat")
  const [longOnly, setLongOnly] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [attached, setAttached] = useState<Runner | null>(null)
  const [playError, setPlayError] = useState<string | null>(null)
  const draftKey = draftKeyOfStrategy(strategy.id)
  /** null while a draft is being checked against its saved version. */
  const [target, setTarget] = useState<PaperTradeTarget | null>(() =>
    draftKey === null ? paperTradeTarget(strategy.id, null, null) : null
  )

  useEffect(() => {
    if (draftKey === null) {
      setTarget(paperTradeTarget(strategy.id, null, null))
      return
    }
    let live = true
    setTarget(null)
    draftTarget(strategy.id, draftKey).then(
      (resolved) => live && setTarget(resolved),
      () => live && setTarget({ _tag: "Unsaved", message: SAVE_FIRST, saved: null })
    )
    return () => {
      live = false
    }
  }, [strategy.id, draftKey])

  useEffect(() => {
    let live = true
    call((client) => client.accounts.list()).then(
      (all) => {
        if (!live) return
        const eligible = eligibleAccounts(all, instrument)
        setAccounts(eligible)
        setAccountId((current) => current || (eligible[0]?.id ?? ""))
      },
      (failure: unknown) => live && setLoadError(attachErrorText(errorTag(failure), null))
    )
    return () => {
      live = false
    }
  }, [instrument])

  const account = useMemo(() => accounts?.find((candidate) => candidate.id === accountId), [accounts, accountId])
  const shortable = canShort(product)

  const submit = async () => {
    if (account === undefined || target?._tag !== "Ready") return
    const request = attachRequest({
      strategyId: target.strategyId,
      instrument,
      product,
      startPolicy,
      direction: shortable && !longOnly ? "all" : "long",
      inputs: strategy.inputs,
      properties: strategy.properties,
      timeframe: runnerTimeframe(account, chartTimeframe)
    })
    if (Result.isFailure(request)) {
      setError(request.failure)
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      setAttached(await call((client) => client.accounts.attachRunner({ params: { id: account.id }, payload: request.success })))
    } catch (failure) {
      const detail = typeof failure === "object" && failure !== null && "detail" in failure ? failure.detail : null
      setError(attachErrorText(errorTag(failure), detail))
    } finally {
      setSubmitting(false)
    }
  }

  /** An account made from this dialog ("Create a paper account"): offered, and chosen. */
  const accountCreated = (created: AccountSummary) => {
    if (eligibleAccounts([created], instrument).length === 0) return
    setAccounts((current) => [...(current ?? []).filter((a) => a.id !== created.id), created])
    setAccountId(created.id)
  }

  /**
   * After attaching: shows the account in the Paper Trading panel (its Strategies
   * tab) and, for a paused replay, plays it, so the strategy's trades start to show.
   */
  const showAndPlay = async (): Promise<boolean> => {
    if (account === undefined) return false
    accountsStore.select(account.id)
    tabPreference.set("strategies")
    collapsedPreference.set("false")
    testerPanel.setCollapsed(true)
    if (account.clock._tag !== "Replay" || account.clock.status === "Playing") return true
    try {
      await tradingApi.clock(account.id, { _tag: "Play", speed: 5, follow: shownInstruments(currentWorkspace()) })
      return true
    } catch (failure) {
      setPlayError(`The replay didn't start (${errorTag(failure)}): press Play in Paper Trading.`)
      return false
    }
  }

  return {
    target,
    accountCreated,
    showAndPlay,
    playError,
    /** Paper trade the saved version instead of an unsaved draft. */
    useSavedVersion: () => {
      if (target?._tag !== "Unsaved" || target.saved === null) return
      const { strategyId, version } = target.saved
      setTarget({ _tag: "Ready", strategyId: `user:${strategyId}@${version}`, note: `It paper trades the saved version ${version}, without the draft's changes.` })
    },
    accounts,
    loadError,
    account,
    setAccountId,
    products: productsFor(instrument),
    product,
    setProduct,
    startPolicy,
    setStartPolicy,
    shortable,
    longOnly: !shortable || longOnly,
    setLongOnly,
    submitting,
    error,
    attached,
    submit
  }
}
